/**
 * A tela de consumo de IA — a parte sem React.
 *
 * O que decide número e rótulo mora aqui, testável sem renderizar:
 * como a série vira pontos do `LineChart`, como um ranking vira barras
 * (custo quando há custo; chamadas quando não há), o que cada KPI
 * mostra quando o custo não foi apurado, e os rótulos dos dois enums
 * fechados da lista. A `AiUsagePage` só liga isso aos controles.
 *
 * **Custo nulo NUNCA vira zero** em nenhuma conta daqui — a regra é a
 * mesma da valoração (`0` é valor, `null` é ausência). Um ranking por
 * custo com uma fatia sem preço não a some: ela vai pro fim, com a
 * marca de "sem custo apurado", e a tela diz quantas chamadas do
 * recorte ficaram sem preço.
 */

import type {
  AiUsageCall,
  AiUsageCostStatus,
  AiUsageGroupSlice,
  AiUsageOption,
  AiUsageMetric,
  AiUsageModelSlice,
  AiUsageOutcome,
  AiUsageTimelinePoint
} from "@/shared/api/api-client";
import type { LinePoint } from "@/shared/components/charts/LineChart";
import type { RankedItem } from "@/shared/components/charts/RankedBars";
import { formatCompact, formatInteger } from "@/shared/utils/format";
import { formatMoney } from "@/shared/utils/format-currency";
import { shiftDays } from "@/shared/utils/installation-clock";

// --- KPIs -----------------------------------------------------------------

/** Chamadas que de fato foram à IA. É o próprio `calls`: no servidor
 *  ele conta só linha COM modelo respondido, e os dois baldes sem
 *  chamada (`resolved_without_ai`, `not_called_by_config`) ficam FORA
 *  dele — a primeira versão desta função os subtraía e o card mostrou
 *  "-159" no devstack. Existe como função pra o significado ter nome. */
export function aiCalls(metric: Pick<AiUsageMetric, "calls">): number {
  return metric.calls;
}

export function totalTokens(metric: Pick<AiUsageMetric, "input_tokens" | "output_tokens">): number | null {
  if (metric.input_tokens === null && metric.output_tokens === null) return null;
  return (metric.input_tokens ?? 0) + (metric.output_tokens ?? 0);
}

/** O que o card de custo diz embaixo do número. Três casos: tudo
 *  apurado; parte apurada (o número é PARCIAL, e a linha diz quantas
 *  ficaram de fora); nada apurado. */
export function costContext(metric: Pick<AiUsageMetric, "priced_calls" | "unpriced_calls">): string {
  if (metric.unpriced_calls === 0) return "pelo preço da época de cada chamada";
  if (metric.priced_calls === 0) return "nenhuma chamada do período tem preço";
  return `parcial — ${formatInteger(metric.unpriced_calls)} sem preço na época`;
}

/** "US$ 9,63", ou "sem custo apurado" quando é `null`. */
export function costLabel(cost: string | null, currency: string, precision = 2): string {
  return cost === null ? "sem custo apurado" : formatMoney(cost, currency, precision);
}

// --- a série ---------------------------------------------------------------

export const TIMELINE_METRICS = ["cost", "calls", "tokens"] as const;
export type TimelineMetric = (typeof TIMELINE_METRICS)[number];

export const TIMELINE_METRIC_LABELS: Record<TimelineMetric, string> = {
  cost: "Custo",
  calls: "Chamadas",
  tokens: "Tokens"
};

/** `AAAA-MM-DD` → `DD/MM`, sem `Date`: o dia já é local, e um `Date`
 *  aqui o deslocaria pro fuso do navegador. */
export function dayLabel(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

const DIA_VAZIO: AiUsageMetric = {
  calls: 0,
  succeeded: 0,
  failed: 0,
  unfinished: 0,
  resolved_without_ai: 0,
  not_called_by_config: 0,
  tokenless_calls: 0,
  unpriced_calls: 0,
  priced_calls: 0,
  input_tokens: null,
  output_tokens: null,
  cost: null
};

/** A série do servidor só tem os dias COM linha; o gráfico precisa de
 *  todos os dias da janela, senão "últimos 30 dias" com cinco dias de
 *  uso vira cinco pontos colados e a distância entre eles mente sobre
 *  o tempo. Dia ausente entra vazio (custo e tokens `null`, contadores
 *  zero) — é o que ele é. Teto de 400 dias por segurança: a janela é
 *  digitada, e uma de dez anos travaria o SVG. */
export function fillDays(timeline: AiUsageTimelinePoint[], from: string, to: string): AiUsageTimelinePoint[] {
  const porDia = new Map(timeline.map(point => [point.day, point]));
  const [inicio, fim] = from <= to ? [from, to] : [to, from];
  const dias: AiUsageTimelinePoint[] = [];
  for (let dia = inicio, n = 0; dia <= fim && n < 400; dia = shiftDays(dia, 1), n++) {
    dias.push(porDia.get(dia) ?? { ...DIA_VAZIO, day: dia });
  }
  return dias;
}

/** Os pontos do gráfico. Dia sem custo apurado vale ZERO na série de
 *  custo — mesma decisão do dashboard pra valoração: furar a linha no
 *  meio seria pior que desenhá-la no chão. A distinção null-vs-zero
 *  mora no card e na lista, onde significa alguma coisa. */
export function timelinePoints(timeline: AiUsageTimelinePoint[], metric: TimelineMetric): LinePoint[] {
  return timeline.map(point => ({
    key: point.day,
    label: dayLabel(point.day),
    value:
      metric === "cost"
        ? Number(point.cost ?? 0)
        : metric === "calls"
          ? aiCalls(point)
          : (totalTokens(point) ?? 0)
  }));
}

export function timelineFormatter(metric: TimelineMetric, currency: string): (value: number) => string {
  if (metric === "cost") return value => formatMoney(value, currency);
  if (metric === "calls") return formatInteger;
  return formatCompact;
}

// --- rankings ---------------------------------------------------------------

export type RankedBy = "cost" | "calls";

type Rankable = { key: string; cost: string | null; calls: number; unpriced_calls: number };

/** Por custo quando ALGUMA fatia tem custo; por chamadas quando nenhuma
 *  tem — senão a tela de uma instalação sem preço cadastrado mostraria
 *  todo ranking vazio. A escolha é devolvida pra tela dizer qual foi. */
export function rankingBasis(slices: Rankable[]): RankedBy {
  return slices.some(slice => slice.cost !== null) ? "cost" : "calls";
}

/** Barras do ranking. Por custo, a fatia sem custo vai pro FIM, com
 *  valor zero — ela existe (a tela lista `unpriced_calls` ao lado),
 *  mas não tem como disputar posição num ranking de dinheiro. */
export function rankedItems<T extends Rankable>(
  slices: T[],
  by: RankedBy,
  label: (slice: T) => string
): RankedItem[] {
  const ordenadas = [...slices].sort((a, b) => {
    if (by === "calls") return b.calls - a.calls;
    const ca = a.cost === null ? -1 : Number(a.cost);
    const cb = b.cost === null ? -1 : Number(b.cost);
    return cb - ca || b.calls - a.calls;
  });
  return ordenadas.map(slice => ({
    key: slice.key,
    label: label(slice),
    value: by === "cost" ? Number(slice.cost ?? 0) : slice.calls
  }));
}

export function rankingNoun(by: RankedBy): string {
  return by === "cost" ? "de custo" : "chamadas";
}

export function modelLabel(slice: AiUsageModelSlice): string {
  return slice.catalog_name ?? slice.key;
}

export function groupLabel(slice: AiUsageGroupSlice): string {
  return slice.user_name || slice.label || slice.key;
}

/** A opção de filtro (empresa ou pessoa): o nome da pessoa quando há,
 *  senão o rótulo que o servidor mandou (o código). */
export function optionLabel(option: AiUsageOption): string {
  return option.user_name || option.label;
}

/** O código da pessoa no tooltip — só quando o nome o substituiu. */
export function callOwnerTitle(call: AiUsageCall): string | undefined {
  return !call.company_name && call.user_id && call.user_name ? call.user_id : undefined;
}

// --- a lista ------------------------------------------------------------------

export const OUTCOME_LABELS: Record<AiUsageOutcome, string> = {
  ok: "Concluída",
  failed: "Falhou",
  unfinished: "Não terminou",
  resolved_without_ai: "Resolvida sem IA",
  not_called: "Não chamou por configuração"
};

/** A classe do selo — forma além de cor vem do TEXTO, que é sempre
 *  diferente; a cor só reforça. */
export const OUTCOME_TONE: Record<AiUsageOutcome, "success" | "warning" | "inactive" | "active"> = {
  ok: "success",
  failed: "warning",
  unfinished: "warning",
  resolved_without_ai: "inactive",
  not_called: "inactive"
};

export const COST_STATUS_LABELS: Record<AiUsageCostStatus, string> = {
  priced: "",
  no_price: "sem preço na época",
  no_tokens: "sem tokens registrados",
  unfinished: "não terminou",
  no_call: "sem chamada"
};

/** O que a célula de custo da linha mostra: o valor com 4 casas, ou o
 *  MOTIVO de não haver valor. */
export function callCostLabel(call: AiUsageCall, currency: string): string {
  return call.cost_status === "priced"
    ? formatMoney(call.cost, currency, 4)
    : COST_STATUS_LABELS[call.cost_status];
}

/** Quem pagou a linha: a empresa (esteira) ou a pessoa (Copiloto,
 *  autofill). A pessoa aparece pelo nome do cadastro local e, sem
 *  cadastro, pelo código (o `sub` do token). */
export function callOwnerLabel(call: AiUsageCall): string {
  if (call.company_name) return call.brand_name ? `${call.company_name} · ${call.brand_name}` : call.company_name;
  if (call.user_id) return call.user_name || call.user_id;
  return "—";
}

/** "gpt-4o-mini → gpt-4o-mini-2024-07-18" quando o respondido difere
 *  do pedido; só um nome quando são iguais ou só há um. */
export function callModelLabel(call: AiUsageCall): string {
  const pedido = call.requested_model;
  const respondido = call.model;
  if (pedido && respondido && pedido.toLowerCase() !== respondido.toLowerCase()) return `${pedido} → ${respondido}`;
  return respondido ?? pedido ?? "—";
}
